/**
 * firebase.js — Lazy Firebase Auth bootstrap.
 *
 * Activates only when ALL of these are true:
 *   1. `firebase` npm package is installed (currently NOT installed — graceful no-op)
 *   2. `VITE_FIREBASE_API_KEY` env var is set (not placeholder)
 *   3. `VITE_FIREBASE_PROJECT_ID` env var is set
 *
 * Otherwise falls back to mock mode → AuthContext keeps using localStorage auth.
 *
 * To activate (when Oney provides credentials):
 *   1. npm install firebase
 *   2. Set env vars in .env.production:
 *        VITE_FIREBASE_API_KEY=...
 *        VITE_FIREBASE_AUTH_DOMAIN=omnisio-prod.firebaseapp.com
 *        VITE_FIREBASE_PROJECT_ID=omnisio-prod
 *        VITE_FIREBASE_APP_ID=...
 *   3. No code changes needed — this module activates automatically.
 */

const config = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
}

function isConfigured() {
  return (
    config.apiKey &&
    !config.apiKey.includes('your-') &&
    !config.apiKey.includes('placeholder') &&
    config.projectId
  )
}

let _app = null
let _auth = null
let _loadAttempted = false
let _loadFailed = false

/** Returns the Firebase Auth instance, or null if not configured / SDK missing. */
export async function getFirebaseAuth() {
  if (_auth) return _auth
  if (_loadFailed || !isConfigured()) return null
  if (_loadAttempted) return null

  _loadAttempted = true
  try {
    // Dynamic import — fails gracefully if `firebase` is not installed
    const { initializeApp, getApps, getApp } = await import('firebase/app')
    const { getAuth } = await import('firebase/auth')

    _app = getApps().length === 0 ? initializeApp(config) : getApp()
    _auth = getAuth(_app)
    return _auth
  } catch (err) {
    _loadFailed = true
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        '[firebase] SDK not installed or config missing — running in mock auth mode.\n' +
        'To enable real cross-app SSO: npm install firebase, set VITE_FIREBASE_API_KEY.'
      )
    }
    return null
  }
}

/**
 * Get a fresh Firebase ID token for the current user.
 * Returns null if Firebase not configured or no user signed in.
 *
 * Used by `omnisio-api.js` and `AuthContext.getIdToken()`.
 */
export async function getFirebaseIdToken() {
  const auth = await getFirebaseAuth()
  if (!auth?.currentUser) return null
  try {
    return await auth.currentUser.getIdToken(/* forceRefresh */ false)
  } catch {
    return null
  }
}

/**
 * Sign in with email/password (returns user object or throws).
 * Lazy-loads firebase/auth functions.
 */
export async function firebaseSignInWithEmail(email, password) {
  const auth = await getFirebaseAuth()
  if (!auth) throw new Error('firebase-not-configured')
  const { signInWithEmailAndPassword } = await import('firebase/auth')
  const cred = await signInWithEmailAndPassword(auth, email, password)
  return cred.user
}

/** Create a new user (returns user). */
export async function firebaseCreateUserWithEmail(email, password) {
  const auth = await getFirebaseAuth()
  if (!auth) throw new Error('firebase-not-configured')
  const { createUserWithEmailAndPassword } = await import('firebase/auth')
  const cred = await createUserWithEmailAndPassword(auth, email, password)
  return cred.user
}

/** Send password reset email. */
export async function firebaseSendPasswordReset(email) {
  const auth = await getFirebaseAuth()
  if (!auth) throw new Error('firebase-not-configured')
  const { sendPasswordResetEmail } = await import('firebase/auth')
  await sendPasswordResetEmail(auth, email)
}

/** Sign out. */
export async function firebaseSignOut() {
  const auth = await getFirebaseAuth()
  if (!auth) return
  const { signOut } = await import('firebase/auth')
  await signOut(auth)
}

/**
 * Subscribe to auth state changes.
 * Returns an unsubscribe function, or a no-op if Firebase not configured.
 */
export function onFirebaseAuthChanged(callback) {
  let unsub = () => {}
  let cancelled = false

  ;(async () => {
    const auth = await getFirebaseAuth()
    if (!auth || cancelled) return
    const { onAuthStateChanged } = await import('firebase/auth')
    unsub = onAuthStateChanged(auth, callback)
  })()

  return () => {
    cancelled = true
    unsub()
  }
}

/** Sign in with Google (popup). Returns user or throws. */
export async function firebaseSignInWithGoogle() {
  const auth = await getFirebaseAuth()
  if (!auth) throw new Error('firebase-not-configured')
  const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth')
  const provider = new GoogleAuthProvider()
  const cred = await signInWithPopup(auth, provider)
  return cred.user
}

/** Sign in with Apple (popup). Returns user or throws. */
export async function firebaseSignInWithApple() {
  const auth = await getFirebaseAuth()
  if (!auth) throw new Error('firebase-not-configured')
  const { OAuthProvider, signInWithPopup } = await import('firebase/auth')
  const provider = new OAuthProvider('apple.com')
  provider.addScope('email')
  provider.addScope('name')
  const cred = await signInWithPopup(auth, provider)
  return cred.user
}

/**
 * Subscribe to ID token refresh — fires on sign-in, sign-out, and hourly token
 * refresh. Use to keep a cached token fresh (addendum spec § 9).
 */
export function onFirebaseIdTokenChanged(callback) {
  let unsub = () => {}
  let cancelled = false
  ;(async () => {
    const auth = await getFirebaseAuth()
    if (!auth || cancelled) return
    const { onIdTokenChanged } = await import('firebase/auth')
    unsub = onIdTokenChanged(auth, callback)
  })()
  return () => { cancelled = true; unsub() }
}

export function isFirebaseEnabled() {
  return isConfigured() && !_loadFailed
}
